<template>
  <header
    class="header bg__color--white"
    :class="{ 'is-fixed': show_mobile_menu }"
  >
    <div class="container d-none d-md-flex justify-content-end margin__b--12">
      <language-switcher class="margin__r--24" />

      <app-session-button
        class="session"
        @click="show_mobile_menu = false"
        @close="show_mobile_menu = false"
      />
    </div>

    <div class="container d-flex align-items-center header__wrapper">
      <router-link
        :to="{ name: 'Home', params: { locale } }"
        class="logo"
      >
        <img
          class="logo__img"
          :src="translate(logo) || client.logo.url"
          :alt="client.name"
        >
      </router-link>

      <nav
        class="navigation d-none d-lg-block"
        :class="{ 'd-block': show_mobile_menu }"
      >
        <ul class="navigation__ul d-md-flex align-items-center">
          <li
            v-for="({ link }, index) in navigation"
            :key="`navigation_${index}`"
            class="navigation__li"
          >
            <app-link
              :link="link"
              class="navigation__link link__color--sec-prim link--naked"
            />
          </li>

          <li class="navigation__li d-md-none margin__t--24">
            <app-link
              :link="cta1"
              class="link__color--sec-prim link--naked bold"
            />
          </li>

          <li class="navigation__li d-md-none">
            <app-link
              :link="cta2"
              class="cta padding__tb--12 padding__lr--24"
            />
          </li>

          <li class="navigation__li d-md-none margin__t--24">
            <app-session-button
              class="session"
              @click="show_mobile_menu = false"
              @close="show_mobile_menu = false"
            />
          </li>

          <li class="navigation__li d-flex d-md-none justify-content-start">
            <language-switcher position-x="left" />
          </li>
        </ul>
      </nav>

      <div class="header__actions">
        <app-link
          :link="cta1"
          class="link__color--sec-prim link--naked bold d-none d-md-block"
        />

        <app-link
          :link="cta2"
          class="cta padding__tb--12 padding__lr--24 d-none d-md-block margin__l--12"
        />

        <app-burger-menu
          class="header__burger d-lg-none"
          :state="show_mobile_menu"
          @toggle="toggleMobileMenu"
        />
      </div>
    </div>
  </header>
</template>

<script setup>
import { ref, watch, defineAsyncComponent } from 'vue'
import { storeToRefs } from 'pinia'
import { useClientStore } from '@/stores/ClientStore'
import { useLanguageStore } from '@/stores/LanguageStore'
import { useI18n } from '@/vendors/i18n'
import { useRoute } from 'vue-router'
import { scrollTo } from '@/utils/utils'

import AppSessionButton from '&/organisms/AppSessionButton'
const AppLink = defineAsyncComponent(() => import('&/atoms/AppLink'))
const AppBurgerMenu = defineAsyncComponent(() => import('&/atoms/AppBurgerMenu'))
const LanguageSwitcher = defineAsyncComponent(() => import('&/atoms/LanguageSwitcher'))

const { section } = defineProps({
  section: { type: Object, required: true }
})

const route = useRoute()
const { translate } = useI18n()
const { getClient } = useClientStore()
const { locale } = storeToRefs(useLanguageStore())

const { client } = getClient()

const { logo = {}, navigation = [], cta1 = {}, cta2 = {} } = section.attributes

const show_mobile_menu = ref(true)

const toggleMobileMenu = () => {
  scrollTo()

  setTimeout(() => show_mobile_menu.value = !show_mobile_menu.value, 100)
}

watch(route, () => show_mobile_menu.value = false)
</script>

<style lang="scss" scoped>
$color--primary: #adab00;

:deep(.cta) {
  background-color: $color--primary;
  border-radius: $radius__buttons;
  color: $white;
  cursor: pointer;
  display: inline-block;
  font-weight: normal;
  position: relative;
  transition: all $transition__duration;

  &:hover,
  &:focus {
    background-color: #797700;
  }
}

.header {
  position: relative;
  padding: 6px 0;

  &.is-fixed {
    position: fixed;
    width: 100%;
    top: 0;
    z-index: 9999;

    @include mq(md) {
      position: relative;
    }
  }

  @include mq(md) {
    padding: 12px 0;
  }

  &__wrapper {
    gap: 12px;
    justify-content: space-between;

    @include mq(lg) {
      gap: 36px;
    }
  }

  &__actions {
    display: flex;
    align-items: center;
    flex: 0 0 auto;
  }
}

.logo {
  flex: 0 0 auto;

  &__img {
    display: block;
    height: auto;
    max-height: 50px;
    max-width: 120px;
    width: 100%;

    @include mq(md) {
      max-width: 200px;
    }
  }
}

.navigation {
  overflow: auto;
  scrollbar-width: thin;
  scrollbar-color: $color--secondary $white;
  position: fixed;
  top: 57px;
  left: 0;
  width: 100vw;
  height: calc(100vh - 57px);
  background: $white;
  z-index: 777;
  padding: 24px 0 60px;
  background: $white;

  @include mq(md) {
    position: absolute;
    top: inherit;
    bottom: 0;
    transform: translateY(100%);
    height: auto;
    padding: 12px 0;
  }

  @include mq(lg) {
    position: relative;
    height: auto;
    top: 0;
    padding: 12px 0;
    width: auto;
    transform: none;
    flex: 1 1 auto;
  }

  &__ul {
    gap: 24px;
    max-width: 720px;
    margin: 0 auto;
    padding: 0 12px;

    @include mq(md) {
      gap: 12px;
      white-space: nowrap;
      margin: 0 auto;
      width: max-content;
      max-width: inherit;
    }

    @include mq(lg) {
      margin: 0;
    }
  }

  &__li {
    &:not(:last-child) {
      margin-bottom: 12px;

      @include mq(md) {
        margin: 0;
      }
    }
  }

  :deep() {
    .navigation__link {
      font-size: rem(20px);

      @include mq(md) {
        font-size: rem(16px);
      }

      &.is-active {
        color: $color--primary;
      }
    }
  }
}

:deep() {
  .session {
    font-size: rem(14px);
  }
}
</style>
